import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import Button from "../../../../../components/Common/Button/Button";
import Input from "../../../../../components/Common/Input/Input";
import { distributePollResponse } from "../../../../../store/actions/actionCreators/socialActionCreator";
import { setErrorHandler } from "../../../../../store/actions/actionHandlers/errorActionHandler";
import {
  distributePollHandler,
  getPollUserGroupsSocialHandler,
  getSearchPollGroupHandler,
  getSearchPollUserHandler,
  getTenantPollUsersHandler,
} from "../../../../../store/actions/actionHandlers/socialActionHandler";
import { errorTypes } from "../../../../../utils/errorTypes";
import classes from "./DistributePollForm.module.scss";
import { FormattedMessage } from "react-intl";

const convert12hrFormat = (time) => {
  // Check correct time format and split into components
  time = time?.toString().match(/^([01]\d|2[0-3])(:)([0-5]\d)(:[0-5]\d)?$/) || [
    time,
  ];

  if (time?.length > 1) {
    // If time format correct
    time = time?.slice(1); // Remove full string match value
    time[5] = +time[0] < 12 ? " AM" : " PM"; // Set AM/PM
    time[0] = +time[0] % 12 || 12; // Adjust hours
  }
  return time.join(""); // return adjusted time or original string
};

export const dateFormatyyyymmdd = (date) => {
  const today = date;
  const yyyy = today.getFullYear();
  let mm = today.getMonth() + 1;
  let dd = today.getDate();

  if (dd < 10) dd = "0" + dd;
  if (mm < 10) mm = "0" + mm;

  return yyyy + "-" + mm + "-" + dd;
};

export const dateFormatmmddyyyy = (date) => {
  const today = date;
  const yyyy = today.getFullYear();
  let mm = today.getMonth() + 1;
  let dd = today.getDate();

  if (dd < 10) dd = "0" + dd;
  if (mm < 10) mm = "0" + mm;

  return mm + "/" + dd + "/" + yyyy;
};

const DistributePollForm = ({ activeTab, pollId, setisDistibuteModal }) => {
  const { WARN } = errorTypes;
  const dispatch = useDispatch();
  const methods = useForm({});
  const { setValue } = methods;
  const socialState = useSelector((state) => state.socialReducer);
  const { pollPreviewList, distributePollResponseData, pollLoading } =
    socialState;
  const [fromMinDate, setFromMinDate] = useState(
    dateFormatyyyymmdd(new Date()),
  );
  const [toMinDate, setToMinDate] = useState(dateFormatyyyymmdd(new Date()));
  const [fromMaxDate, setFromMaxDate] = useState(null);
  const [toMaxDate, setToMaxDate] = useState(null);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [fromTime, setFromTime] = useState(null);
  const [toTime, setToTime] = useState(null);
  const [autoAssignToNewUsers, setAutoAssignToNewUsers] = useState(false);

  const handleSearchChange = (e) => {
    if (activeTab === 1) {
      if (e.target.value)
        dispatch(getSearchPollUserHandler(e.target.value, pollPreviewList));
      else dispatch(getTenantPollUsersHandler(true, pollPreviewList));
    }
    if (activeTab === 2) {
      if (e.target.value)
        dispatch(getSearchPollGroupHandler(e.target.value, pollPreviewList));
      else {
        dispatch(getPollUserGroupsSocialHandler(true, pollPreviewList));
      }
    }
  };

  const handleFromDate = (e) => {
    setFromDate(e.target.value);
    if (e.target.value) setToMinDate(e.target.value);
  };

  const handleToDate = (e) => {
    setToDate(e.target.value);
    if (e.target.value) setFromMaxDate(e.target.value);
  };

  const handleDistributeClick = () => {
    if (pollPreviewList.length === 0) {
      dispatch(
        setErrorHandler(WARN, "Please Select atleast one User or User Group"),
      );
    } else {
      const distributePollObj = {
        DistributeUserModel: [],
        EndDate: "",
        EndDateTime: "",
        IsNotify: true,
        PollId: 0,
        StartDate: "",
        StartDateTime: "",
      };

      // Only add AutoAssignToNewUsers property when checkbox is checked
      if (autoAssignToNewUsers) {
        distributePollObj.IsAutoDistribute = true;
      }

      const distributeUserModel = [];
      pollPreviewList?.forEach((item) => {
        if (item.type === "User")
          distributeUserModel.push({
            Id: item?.Id ? item?.Id : item?.ID,
            Name: item?.FirstName + "(" + item?.LoginId + ")",
            Type: "User",
          });
        else
          distributeUserModel.push({
            Name: item?.Name,
            Type: "UserGroup",
          });
      });
      distributePollObj.EndDate = dateFormatmmddyyyy(new Date(toDate));
      distributePollObj.EndDateTime = toTime
        ? convert12hrFormat(toTime)
        : "11:59 PM";
      distributePollObj.PollId = pollId;
      distributePollObj.StartDate = dateFormatmmddyyyy(new Date(fromDate));
      distributePollObj.StartDateTime = fromTime
        ? convert12hrFormat(fromTime)
        : "12:00 AM";
      distributePollObj.DistributeUserModel = distributeUserModel;
      dispatch(distributePollHandler(distributePollObj));
    }
  };

  const resetForm = () => {
    setToMinDate(dateFormatyyyymmdd(new Date()));
    setFromMinDate(dateFormatyyyymmdd(new Date()));
    setFromMaxDate(null);
    setToMaxDate(null);
    setFromDate(null);
    setToDate(null);
    setFromTime(null);
    setToTime(null);
    setAutoAssignToNewUsers(false);
    setValue("Search", null);
    setValue("fromDate", null);
    setValue("fromTime", null);
    setValue("toDate", null);
    setValue("toTime", null);
    if (activeTab === 1)
      dispatch(getTenantPollUsersHandler(true, pollPreviewList));
    if (activeTab === 2)
      dispatch(getPollUserGroupsSocialHandler(true, pollPreviewList));
  };

  useEffect(() => {
    if (distributePollResponseData) {
      setisDistibuteModal(false);
      dispatch(distributePollResponse(null));
    }
  }, [distributePollResponseData, setisDistibuteModal, dispatch]);

  useEffect(() => {
    if (activeTab) setValue("Search", "");
  }, [activeTab, setValue]);

  return (
    <FormProvider {...methods}>
      {/* Show auto assign checkbox only for usergroup tab (activeTab === 2) */}
      {activeTab === 2 && (
        <div className={classes.autoAssignCheckbox}>
          <Input
            name="autoAssignToNewUsers"
            label="Auto‑assign poll to new users in selected user group. ( while active )"
            type="checkbox"
            labelColor="light"
            direction="row-reverse"
            width="2"
            height="2"
            checked={autoAssignToNewUsers}
            onChange={(e) => setAutoAssignToNewUsers(e.target.checked)}
          />
        </div>
      )}
      <div className={classes.distributeForm}>
        <div className={classes.hiddenLabelStyle}>
          <Input
            name="Search"
            label={<FormattedMessage id="P_Search" />}
            placeholder={"Search User Or User Groups"}
            labelColor="light"
            search={true}
            borderRadius={1}
            height={5}
            width={25}
            onChange={handleSearchChange}
            autoComplete="off"
            disabled={activeTab === 3}
          />
        </div>
        <div className={classes.dateStyle}>
          <Input
            type="date"
            name="fromDate"
            label="From Date"
            labelColor="light"
            borderRadius={1}
            height={4}
            width={13}
            min={fromMinDate}
            max={fromMaxDate}
            onChange={handleFromDate}
            required
            onKeyDown={() => {
              return false;
            }}
          />
          <div className={classes.hiddenLabelStyle}>
            <Input
              type="time"
              name="fromTime"
              label="From Time"
              labelColor="light"
              borderRadius={1}
              height={4}
              width={13}
              onKeyDown={() => {
                return false;
              }}
              min="10:00"
              onChange={(e) => setFromTime(e.target.value)}
            />
          </div>
        </div>
        <div className={classes.dateStyle}>
          <Input
            type="date"
            name="toDate"
            label="To Date"
            labelColor="light"
            borderRadius={1}
            height={4}
            width={13}
            min={toMinDate}
            max={toMaxDate}
            onChange={handleToDate}
            required
            onKeyDown={() => {
              return false;
            }}
          />
          <div className={classes.hiddenLabelStyle}>
            <Input
              type="time"
              name="toTime"
              label="To Time"
              labelColor="light"
              borderRadius={1}
              height={4}
              width={13}
              onKeyDown={() => {
                return false;
              }}
              onChange={(e) => setToTime(e.target.value)}
            />
          </div>
        </div>
        <div className={classes.distributeBtn}>
          <span className={classes.clear} onClick={resetForm}>
            <FormattedMessage id="RESET_MSG" />
          </span>
          <Button
            type="submit"
            label={<FormattedMessage id="P_DISTRIBUTE" />}
            dark
            width="11"
            height="4.5"
            disabled={fromDate && toDate ? false : true}
            onClick={handleDistributeClick}
            backgroundColor={pollLoading ? undefined : "#D31B61"}
            loading={pollLoading}
          />
        </div>
      </div>
    </FormProvider>
  );
};

export default DistributePollForm;
